import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import EventsClient from 'lib/clients/EventsClient';
import { MediaRecorderClient } from 'lib/clients/mediaRecorder';
import { LocalMediaRecorderClient } from 'lib/clients/mediaRecorder/LocalMediaRecorderClient';
import {
  ICE_CANDIDATE_GATHERING_ERROR,
  WebrtcMediaRecorderClient,
} from 'lib/clients/mediaRecorder/WebrtcMediaRecorderClient';
import { disposeStream } from 'lib/utils/stream';
import {
  GovernmentIdCaptureFileType,
  GovernmentIdVideoCaptureMethod,
  SelfieCaptureFileType,
  SelfieVideoCaptureMethod,
} from 'verify/lib/interfaces/CaptureOption';

// Do 3 attempts because retrying webrtc can sometimes resolve issues
const MAX_VIDEO_ATTEMPTS_VIA_CONFIG = 3;

// Only attempt 1 time when we're doing secret video
const MAX_VIDEO_ATTEMPTS_VIA_FLIPPER = 1;

export const DEFAULT_VIDEO_CAPTURE_METHODS: (
  | SelfieVideoCaptureMethod
  | GovernmentIdVideoCaptureMethod
)[] = ['stream', 'upload'];

class Manager {
  // The number of attempts that a client has been initialized
  private attempts = 0;

  // The current client that is being actively used across routes
  private current?: MediaRecorderClient = undefined;

  // The last client that successfully uploaded a video
  private lastSuccessfulClient?: MediaRecorderClient = undefined;

  // The maximum number of times we attempt video capture
  private maxAttempts: number = MAX_VIDEO_ATTEMPTS_VIA_CONFIG;

  public getNewClient(
    videoCaptureMethods?: (SelfieVideoCaptureMethod | GovernmentIdVideoCaptureMethod)[],
  ): MediaRecorderClient {
    let client;

    if (!videoCaptureMethods) {
      videoCaptureMethods = DEFAULT_VIDEO_CAPTURE_METHODS;
    }

    // If there were some video attempts made before, try to kill them
    if (this.videoAttempts > 0) {
      const lastClient = this.getClientFromAttempts(this.videoAttempts - 1, videoCaptureMethods);
      this.destroyClient(lastClient, true);
    }

    if (this.lastSuccessfulClient) {
      client = this.lastSuccessfulClient;
    } else {
      client = this.getClientFromAttempts(this.videoAttempts, videoCaptureMethods);
    }

    this.current = client;
    this.lastSuccessfulClient = undefined;

    return client;
  }

  public get currentClient(): MediaRecorderClient | undefined {
    return this.current;
  }

  public get videoAttempts(): number {
    return this.attempts;
  }

  public decrementAttempts() {
    this.attempts--;
  }

  public destroy(amplitudePrefix?: string, cleanUpStream = false) {
    if (amplitudePrefix) {
      EventsClient.logAmplitude(`${amplitudePrefix}.video.destroy-client`, {
        type: this.current?.type,
      });
    }

    this.destroyClient(this.current, cleanUpStream);
    this.current = undefined;
  }

  public recordVideoAttempt() {
    this.attempts++;
  }

  public markClientSuccess() {
    this.lastSuccessfulClient = this.currentClient;
    // Decrement attempts if there was a successful one; we only want to keep track of attempts
    // that are unsuccessful in order to trigger the fallback
    this.decrementAttempts();
  }

  public shouldInitializeVideo(
    enabledCaptureFileTypes?: GovernmentIdCaptureFileType[] | SelfieCaptureFileType[],
    featureFlags?: FeatureFlagsMap,
  ): boolean {
    const videoEnabledThroughFlipper = !!(
      featureFlags &&
      featureFlags?.['inquiry:video-background'] &&
      featureFlags?.['inquiry:video-background-percentage']
    );
    const videoEnabledThroughConfig = !!enabledCaptureFileTypes?.includes('video');

    if (videoEnabledThroughFlipper) {
      return this.videoAttempts < MAX_VIDEO_ATTEMPTS_VIA_FLIPPER;
    } else if (videoEnabledThroughConfig) {
      return this.videoAttempts < this.maxAttempts;
    } else {
      return false;
    }
  }

  /**
   * If the configs specify that video capture should be enabled, then use the video capture methods
   * from configs. Or else default to only ['upload'] as we're doing the 'inquiry:video-background' flipper
   */
  public getVideoCaptureMethods(
    enabledCaptureFileTypes?: GovernmentIdCaptureFileType[] | SelfieCaptureFileType[],
    videoCaptureMethodsFromConfigs?: GovernmentIdVideoCaptureMethod[] | SelfieVideoCaptureMethod[],
  ): GovernmentIdVideoCaptureMethod[] | SelfieVideoCaptureMethod[] {
    return enabledCaptureFileTypes?.includes('video') && videoCaptureMethodsFromConfigs
      ? videoCaptureMethodsFromConfigs
      : ['upload'];
  }

  public videoCanBeReattempted(): boolean {
    return this.videoAttempts < this.maxAttempts;
  }

  /**
   * This is checking for an edge case where during webrtc streaming, we have trouble
   * gathering ICE candidates due to strange networking conditions (VPN, iphone plugged in, etc)
   */
  public shouldReinitializeWithUpload(
    error: Error | string,
    videoCaptureMethods?: (SelfieVideoCaptureMethod | GovernmentIdVideoCaptureMethod)[],
  ): boolean {
    return (
      this.currentClient?.type === 'stream' &&
      error instanceof Error &&
      error?.message === ICE_CANDIDATE_GATHERING_ERROR &&
      (videoCaptureMethods || DEFAULT_VIDEO_CAPTURE_METHODS).includes('upload') &&
      this.videoCanBeReattempted()
    );
  }

  /**
   * @deprecated This method is ONLY for tests, do not use for production code
   */
  public resetForTests() {
    this.attempts = 0;
    this.current = undefined;
    this.lastSuccessfulClient = undefined;
  }

  private getClientFromAttempts(
    attempts: number,
    videoCaptureMethods: (SelfieVideoCaptureMethod | GovernmentIdVideoCaptureMethod)[],
  ): MediaRecorderClient {
    const type = videoCaptureMethods[attempts % videoCaptureMethods.length];
    return type === 'stream' ? WebrtcMediaRecorderClient : LocalMediaRecorderClient;
  }

  private destroyClient(client?: MediaRecorderClient, cleanUpStream = false) {
    // We only need to run dispose stream sometimes; for gov id we run getUserMedia
    // but for selfie do not and reuse the stream from <Webcam>
    if (cleanUpStream && client?.stream) {
      disposeStream(client.stream);
    }

    client?.destroy();
  }
}

export const MediaRecorderManager = new Manager();
